import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'snappytweet_impersonating';

function getSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function ImpersonationBanner() {
  const [session, setSession] = useState(() => getSession());
  const navigate = useNavigate();

  const refresh = useCallback(() => {
    setSession(getSession());
  }, []);

  useEffect(() => {
    window.addEventListener('storage', refresh);
    window.addEventListener('impersonation_change', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('impersonation_change', refresh);
    };
  }, [refresh]);

  const handleExit = () => {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new Event('impersonation_change'));
    if (window.opener) {
      window.close();
    } else {
      navigate('/admin/users');
    }
  };

  if (!session) return null;

  const planLabel = session.plan
    ? session.plan.charAt(0).toUpperCase() + session.plan.slice(1)
    : 'Free';

  return (
    <div
      style={{
        position: 'relative',
        top: 0,
        zIndex: 9999,
        width: '100%',
        background: 'linear-gradient(90deg, #b45309 0%, #d97706 50%, #c2410c 100%)',
        boxShadow: '0 2px 12px rgba(180,83,9,0.45)',
      }}
    >
      {/* Top accent line */}
      <div style={{ height: 2, background: 'rgba(255,255,255,0.25)', width: '100%' }} />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 20px',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        {/* Left: indicator + info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Animated pulse dot */}
          <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                display: 'block',
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: '#fef3c7',
                animation: 'imp-pulse 1.5s ease-in-out infinite',
              }}
            />
            <style>{`
              @keyframes imp-pulse {
                0%, 100% { box-shadow: 0 0 0 0 rgba(254,243,199,0.7); opacity: 1; }
                50% { box-shadow: 0 0 0 6px rgba(254,243,199,0); opacity: 0.8; }
              }
            `}</style>
          </span>

          {/* Shield icon */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="rgba(255,255,255,0.9)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>

          <span
            style={{
              color: '#fff',
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: '0.02em',
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
          >
            Admin Impersonation Mode
          </span>

          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>|</span>

          <span
            style={{
              color: 'rgba(255,255,255,0.9)',
              fontSize: 12,
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
          >
            {session.name && (
              <strong style={{ color: '#fff', marginRight: 4 }}>{session.name}</strong>
            )}
            {session.email}
          </span>

          <span
            style={{
              background: 'rgba(255,255,255,0.18)',
              color: '#fef3c7',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 99,
              padding: '1px 8px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
          >
            {planLabel}
          </span>
        </div>

        {/* Exit button */}
        <button
          onClick={handleExit}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: 'rgba(255,255,255,0.15)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: 8,
            padding: '5px 14px',
            fontSize: 12,
            fontWeight: 700,
            fontFamily: 'Inter, system-ui, sans-serif',
            cursor: 'pointer',
            transition: 'background 0.15s, border-color 0.15s',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.28)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)';
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Exit Impersonation
        </button>
      </div>

      {/* Bottom accent line */}
      <div style={{ height: 2, background: 'rgba(0,0,0,0.15)', width: '100%' }} />
    </div>
  );
}
